import { buildManual } from './manualRef';
import { I18N } from './i18n';
import SECTOR from './sectorTours.json';
import PANEL from './panelTours.json';
import TD from './i18nTerm/de.json';
import TEN from './i18nTerm/en.json';
import TIT from './i18nTerm/it.json';
import TES from './i18nTerm/es.json';
import TPT from './i18nTerm/pt.json';
import TFR from './i18nTerm/fr.json';
import { spoken, setOverrides } from './speech/spoken';
import { setStoredPlayer } from './speech/stored';

// read-aloud help: catalogue of all fixed help texts, hashing identical to the backend, one global player
export const TTS_LANGS = ['de', 'en', 'it', 'es', 'pt', 'fr'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api/tts`;
const TERMS = { de: TD, en: TEN, it: TIT, es: TES, pt: TPT, fr: TFR };
export const secText = (s) => [s.title, ...(s.steps || []), s.tip].filter(Boolean).join('. ').replace(/\s+/g, ' ').replace(/\.\./g, '.').trim();
export const tourText = (a, b) => [a, b].filter(Boolean).join('. ').replace(/\.\./g, '.').trim();

function manualItems(lang) {
  const m = buildManual(lang), out = new Map();
  if (m.quick) out.set('quick', `${m.quick.title}. ${m.quick.steps.join('. ')}`);
  m.chapters.forEach((c) => [...c.secs, ...(c.subs || []).flatMap((s) => s.secs)].forEach((s) => out.set(s.key, secText(s))));
  return out;
}
// intro tours: start page, fullscreen map, window tours and sector tool tours (ids identical to TourGuide)
function tourItems(lang, out) {
  const t = I18N[lang];
  [1, 2, 3, 4, 5].forEach((i) => out.set(`tour:main:${i}`, tourText(t[`tour${i}T`], t[`tour${i}B`])));
  Object.entries(t.mapTour || {}).forEach(([k, v]) => out.set(`tour:map:${k}`, tourText(...v)));
  Object.entries(PANEL[lang] || PANEL.en).forEach(([k, v]) => out.set(`tour:panel:${k}`, tourText(...v)));
  Object.entries(SECTOR[lang] || SECTOR.de).forEach(([m, steps]) => steps.forEach((v, i) => out.set(`tour:sec:${m}:${i}`, tourText(...v))));
}
// field hints: all fixed hint / info texts (incl. sector wording), matched on screen by their exact text
export const HINT_RE = /(Hint|Info|Note|Tip|Help|Desc|Disc)\d*$/;
// setup guide texts that are read aloud when the help mode is chosen
const SETUP = ['suHelpVoice', 'suHelpVoiceD', 'suHelpQuietD', 'suHelpNoD', 'a11yVoiceHello', 'suIntro', 'a11yVoiceD'];
function setupItems(lang, out) { SETUP.forEach((k) => typeof I18N[lang][k] === 'string' && out.set(`tour:setup:${k}`, I18N[lang][k])); }
function hintItems(lang, out) {
  const t = I18N[lang], ok = (v) => typeof v === 'string' && v.length >= 25 && !v.includes('{');
  Object.keys(t).filter((k) => HINT_RE.test(k) && ok(t[k])).forEach((k) => out.set(`hint:${k}`, t[k]));
  Object.entries(TERMS[lang]).forEach(([k, v]) => { const m = k.match(/^tm_(\w+?)_(.+)$/); if (m && HINT_RE.test(m[1]) && ok(v)) out.set(`hint:${m[1]}~${m[2]}`, v); });
}
export function helpItems(lang) {
  const out = manualItems(lang);
  tourItems(lang, out);
  setupItems(lang, out);
  hintItems(lang, out);
  return [...out].filter(([, text]) => text).map(([id, text]) => ({ id, lang, text, spoken: spoken(text, lang) }));
}
// texts that still fall back to German (or English for window tours) in another language -> listed, not voiced
export function catalogue() {
  const de = Object.fromEntries(helpItems('de').map((x) => [x.id, x.text])), en = Object.fromEntries(helpItems('en').map((x) => [x.id, x.text]));
  const items = [], untranslated = [];
  TTS_LANGS.forEach((l) => helpItems(l).forEach((x) => (l !== 'de' && (de[x.id] === x.text || (l !== 'en' && x.id.startsWith('tour:panel:') && en[x.id] === x.text)) ? untranslated.push(x) : items.push(x))));
  return { items, untranslated };
}
export const kindOf = (id) => (id.startsWith('tour:') ? 'tour' : id.startsWith('hint:') ? 'hint' : 'manual');

let cfgP = null;
export const ttsConfig = (fresh) => { if (fresh || !cfgP) cfgP = fetch(`${API}/config`).then((r) => r.json()).catch(() => { cfgP = null; return null; }); return cfgP; };
export async function ttsHash(lang, text) {
  const c = await ttsConfig();
  if (!c || !window.crypto?.subtle) return null;
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${lang}|${c.voices[lang]}|${c.model}|${text}`));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 40);
}

const audio = typeof Audio !== 'undefined' ? new Audio() : null;
let cur = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f(cur));
if (audio) { audio.preload = 'none'; audio.onended = () => { cur = null; emit(); }; }
export const onPlayer = (f) => { subs.add(f); return () => subs.delete(f); };
export const stopTts = () => { if (audio) { audio.pause(); audio.currentTime = 0; } cur = null; emit(); };
// only plays stored files (never a generation); synchronous inside the tap so iOS/Android allow audio
const VER = {};
export const ttsUrl = (h) => `${API}/audio/${h}${VER[h] ? `?v=${VER[h]}` : ''}`;
export function playTts(id, h, onMissing) {
  stopTts();
  if (!audio || !h) { onMissing?.(); return; }
  cur = id; emit();
  audio.onerror = () => { if (cur !== id) return; cur = null; emit(); onMissing?.(); };
  audio.muted = false;
  audio.src = ttsUrl(h);
  // blocked by the phone (no tap yet / app webview): fall back to the device voice instead of staying silent
  audio.play().catch((e) => { if (cur === id) { cur = null; emit(); if (e?.name !== 'AbortError') onMissing?.(); } });
}
// Android app / iOS only allow sound after a tap: the first tap unlocks the shared player for later (tour) playback
const SILENT = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
let unlocked = false;
const unlock = () => {
  if (unlocked || !audio || cur) return;
  unlocked = true;
  audio.muted = true; audio.src = SILENT;
  audio.play().then(() => { if (audio.src === SILENT) audio.pause(); }).catch(() => { unlocked = false; });
};
if (audio && typeof document !== 'undefined') ['pointerdown', 'touchend', 'keydown'].forEach((ev) => document.addEventListener(ev, unlock, { capture: true, passive: true }));
// hashes that already have a stored recording (per language, loaded once)
const readyP = {};
export const ttsReady = (lang, fresh) => {
  if (fresh || !readyP[lang]) readyP[lang] = fetch(`${API}/ready/${lang}`).then((r) => r.json()).then((o) => { Object.assign(VER, o); return new Map(Object.entries(o)); }).catch(() => { readyP[lang] = null; return new Map(); });
  return readyP[lang];
};
// pronunciation corrections of the owner (kept offline in localStorage by the rules engine)
export const loadRules = () => fetch(`${API}/rules`).then((r) => r.json()).then((o) => { setOverrides(o); return o; }).catch(() => null);
// any fixed text spoken by the app: stored recording if there is one (synchronous inside the tap), else the device voice
const index = {};
export async function indexStored(lang) {
  if (!TTS_LANGS.includes(lang)) return;
  const ok = await ttsReady(lang), m = new Map();
  await Promise.all(helpItems(lang).map(async (x) => { const h = await ttsHash(lang, x.text); if (h && ok.has(h)) m.set(x.text, [x.id, h]); }));
  index[lang] = m;
}
setStoredPlayer((text, lang) => { const hit = index[lang]?.get(text); if (!hit) return false; playTts(hit[0], hit[1]); return true; });
export const ttsCall = (path, opts = {}) => fetch(`${API}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'X-Owner-Token': localStorage.getItem('giss_owner_token') || '' } }).then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || 'error'); return d; });
